import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, map, share, switchMap } from 'rxjs/operators';
import { IpService } from '../../services/ip-service.service';


/**
 * Requests the BFF to issue or refresh the SunBucks guest JWT cookies
 * (sb_jwt, sb_fp).  Both cookies are HttpOnly — this service never reads
 * or stores a raw token.  JavaScript has zero access to the token value.
 *
 * Uses HttpBackend directly to bypass the SunBucksJwtInterceptor and avoid
 * a circular dependency.  The cookie endpoint needs no auth headers.
 */
@Injectable({ providedIn: 'root' })
export class SunBucksAuthService {
  private readonly http: HttpClient;

  /**
   * Single-flight guard: when a refresh is already in progress, all callers
   * share the same Observable instead of firing parallel BFF token requests.
   * Reset to null when the in-flight call completes or errors.
   */
  private refreshInFlight$: Observable<void> | null = null;

  constructor(
    handler: HttpBackend,
    private ipService: IpService     // ← added; IpService owns the IP fetch + cache
  ) {
    // Bypass the interceptor chain — this call must not be intercepted.
    this.http = new HttpClient(handler);
  }

  /**
   * Fetches the client IP (via IpService, cached via shareReplay), then calls
   * the BFF token endpoint (POST /api/Configuration/sunbucks/token) with that IP.
   * On success the browser receives new HttpOnly sb_jwt and sb_fp cookies
   * scoped to /api/proxy/sunbucks.  The token value is never returned to
   * or stored in JavaScript — the caller only needs to know it succeeded.
   *
   * Single-flight: if a refresh is already in progress, this returns the
   * existing Observable so all concurrent callers share one HTTP request.
   * finalize() is placed before share() so it fires once when the underlying
   * HTTP call completes (not once per subscriber).
   */
  ensureCookies(): Observable<void> {
    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.ipService.getIPAddress().pipe(
        switchMap(ip =>
          this.http.post(
            '/api/Configuration/sunbucks/token',
            {},
            {
              responseType: 'text',
              headers: { 'X-Forwarded-For': ip }  // ← manually set, interceptor won't run here
            }
          )
        ),
        map((): void => undefined),
        finalize(() => { this.refreshInFlight$ = null; }),
        share()
      );
    }
    return this.refreshInFlight$;
  }
}
